import { useEffect, useState } from "react";

/**
 * useExitIntent — dispara cuando el usuario muestra intención de abandonar la página.
 *
 * Desktop: detecta mouseleave del top del viewport (cursor hacia barra de pestañas/cerrar).
 * Mobile: como no hay mouseleave, dispara después de N segundos de inactividad combinada
 *         con un scroll-up (gesto típico de "voy a salir").
 *
 * Opciones:
 *   - disabled: bool — no engancha listeners
 *   - cooldownDays: int — días que esperar entre apariciones consecutivas (default 7)
 *   - mobileIdleMs: int — milisegundos de inactividad antes de armar el trigger mobile (default 30000)
 *
 * Persistencia:
 *   - sessionStorage "kumia_exit_shown" — flag intra-sesión
 *   - localStorage "kumia_exit_dismissed_at" — timestamp del último dismiss (para cooldown)
 *
 * Devuelve: { shouldShow, dismiss }
 */
export default function useExitIntent({
  disabled = false,
  cooldownDays = 7,
  mobileIdleMs = 30000,
} = {}) {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (disabled || typeof window === "undefined") return;

    // Ya se mostró en esta sesión
    if (sessionStorage.getItem("kumia_exit_shown") === "1") return;

    // Está dentro del cooldown post-dismiss
    const dismissedAt = parseInt(localStorage.getItem("kumia_exit_dismissed_at") || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < cooldownDays * 24 * 60 * 60 * 1000) return;

    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    const trigger = () => {
      if (sessionStorage.getItem("kumia_exit_shown") === "1") return;
      sessionStorage.setItem("kumia_exit_shown", "1");
      setShouldShow(true);
    };

    let lastScrollY = window.scrollY;
    let idleTimer = null;

    const onMouseLeave = (e) => {
      // Solo cuando sale por el top (intención de cerrar tab/regresar)
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };

    const resetIdle = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        const armed = true;
        const onScrollAfterIdle = () => {
          if (window.scrollY < lastScrollY - 80) {
            trigger();
            window.removeEventListener("scroll", onScrollAfterIdle);
          }
          lastScrollY = window.scrollY;
        };
        if (armed) window.addEventListener("scroll", onScrollAfterIdle, { passive: true });
      }, mobileIdleMs);
    };

    const onActivity = () => {
      lastScrollY = window.scrollY;
      resetIdle();
    };

    if (isTouchDevice) {
      resetIdle();
      window.addEventListener("touchstart", onActivity, { passive: true });
      window.addEventListener("scroll", onActivity, { passive: true });
    } else {
      document.addEventListener("mouseleave", onMouseLeave);
    }

    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("touchstart", onActivity);
      window.removeEventListener("scroll", onActivity);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, [disabled, cooldownDays, mobileIdleMs]);

  const dismiss = () => {
    setShouldShow(false);
    localStorage.setItem("kumia_exit_dismissed_at", String(Date.now()));
  };

  return { shouldShow, dismiss };
}
